import { ReactNode } from "react";
import Flex from "@/components/Flex";
import Input from "@/components/Input";
import CustomSelect from "@/components/CustomSelect";
import { PatientallergySeverityEnum } from "@/services";
import { AllergyRow } from "../readmission-f2f-types";
import { useSectionRequirement } from "../section-requirement-context";
import {
  ALLERGY_FIELD_FLEX,
  ALLERGY_FIELD_LABELS,
  ALLERGY_NAME_PLACEHOLDER,
  ALLERGY_NOTES_PLACEHOLDER,
  ALLERGY_SEVERITY_PLACEHOLDER,
} from "../readmission-f2f-constant";

export interface AllergyEntryRowProps {
  row: AllergyRow;
  severityOptions: { value: PatientallergySeverityEnum; label: string }[];
  disabled?: boolean;
  /** Inline message under the allergy name. */
  error?: string;
  /** Inline message under the severity select. */
  severityError?: string;
  /** The new-entry row: its fields are validated by the add action, not by Save & Sign. */
  isDraft?: boolean;
  action: ReactNode;
  onChange: (patch: Partial<AllergyRow>) => void;
  onCommit?: (row: AllergyRow) => void;
}

const AllergyEntryRow = ({
  row,
  severityOptions,
  disabled = false,
  error,
  severityError,
  isDraft = false,
  action,
  onChange,
  onCommit,
}: AllergyEntryRowProps) => {
  const { isRequired, missingError } = useSectionRequirement();
  const savedError = (value: unknown) =>
    isDraft ? undefined : missingError(value);
  return (
    <Flex gap={2} alignItems="flex-start" flexWrap="wrap">
      <Input
        label={ALLERGY_FIELD_LABELS.allergy}
        required={isRequired}
        error={error ?? savedError(row.allergy)}
        flexprops={ALLERGY_FIELD_FLEX}
        textFieldProps={{
          fullWidth: true,
          placeholder: ALLERGY_NAME_PLACEHOLDER,
          value: row.allergy,
          disabled,
          onChange: (e) => onChange({ allergy: e.target.value }),
          onBlur: () => onCommit?.(row),
        }}
      />
      <CustomSelect
        label={ALLERGY_FIELD_LABELS.severity}
        required={isRequired}
        error={severityError ?? savedError(row.severity)}
        placeholder={ALLERGY_SEVERITY_PLACEHOLDER}
        options={severityOptions}
        value={row.severity || null}
        disabled={disabled}
        flexProps={ALLERGY_FIELD_FLEX}
        onChange={(value) => {
          const severity = (value ?? "") as PatientallergySeverityEnum | "";
          onChange({ severity });
          onCommit?.({ ...row, severity });
        }}
      />
      <Input
        label={ALLERGY_FIELD_LABELS.notes}
        flexprops={ALLERGY_FIELD_FLEX}
        textFieldProps={{
          fullWidth: true,
          placeholder: ALLERGY_NOTES_PLACEHOLDER,
          value: row.notes,
          disabled,
          onChange: (e) => onChange({ notes: e.target.value }),
          onBlur: () => onCommit?.(row),
        }}
      />
      {action}
    </Flex>
  );
};

export default AllergyEntryRow;
